/**
 * Flag emoji for a 2-letter ISO country code (e.g. "DE" -> 🇩🇪). Returns '' for
 * anything that isn't a 2-letter code, so callers can fall back to text only.
 */
export const flagEmoji = (countryCode: string): string =>
  countryCode.length === 2
    ? String.fromCodePoint(
        ...[...countryCode.toUpperCase()].map(
          (char) => 0x1f1e6 + char.charCodeAt(0) - 65
        )
      )
    : ''

/**
 * Human-readable country name for a 2-letter ISO code (e.g. "GB" -> "United
 * Kingdom"), via the platform's Intl region data. Falls back to the upper-cased
 * code when it isn't a known 2-letter code (or Intl region names are
 * unavailable).
 */
export const countryLabel = (countryCode: string): string => {
  const code = countryCode.toUpperCase()
  if (code.length !== 2) {
    return countryCode
  }
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

/**
 * Splits a market survey name like "Belgium - fr — test (BE)" into a flag and
 * country code (from the trailing 2-letter code) and the display label without
 * that code. Falls back to the raw name, no flag and an empty country when the
 * pattern is absent (e.g. an edited name) — flags/codes are best-effort, text is
 * always shown.
 */
export const parseMarketName = (
  name: string
): { flag: string; label: string; country: string } => {
  const match = name.match(/\(([A-Za-z]{2})\)\s*$/)
  if (!match || match.index === undefined) {
    return { flag: '', label: name, country: '' }
  }
  return {
    flag: flagEmoji(match[1]),
    label: name.slice(0, match.index).trim(),
    country: match[1].toUpperCase()
  }
}

const MARKET_NAME_SEPARATOR = ' — '
export const AUDIENCE_LABEL_MAX_LENGTH = 60

export interface MarketNameParts {
  countryPrefix: string
  audienceLabel: string
  countryCode: string
}

/**
 * Splits a market name into its country prefix, editable audience label and
 * country code. Splits on the FIRST separator, so the prefix is preserved
 * verbatim from the stored name (it comes from the backend's country name and
 * may itself contain a hyphen, e.g. "Belgium - fr") and a label containing a
 * separator round-trips into the label.
 */
export const splitMarketName = (name: string): MarketNameParts => {
  const { label, country } = parseMarketName(name)
  const separatorAt = label.indexOf(MARKET_NAME_SEPARATOR)
  return separatorAt === -1
    ? { countryPrefix: label, audienceLabel: '', countryCode: country }
    : {
        countryPrefix: label.slice(0, separatorAt),
        audienceLabel: label
          .slice(separatorAt + MARKET_NAME_SEPARATOR.length)
          .trim(),
        countryCode: country
      }
}

export const composeMarketName = ({
  countryPrefix,
  audienceLabel,
  countryCode
}: MarketNameParts): string => {
  const label = audienceLabel.trim()
  return `${countryPrefix}${label ? `${MARKET_NAME_SEPARATOR}${label}` : ''}${
    countryCode ? ` (${countryCode})` : ''
  }`
}

/**
 * Whether a survey name is a market name rather than a project title. Requires
 * the prefix to be the trailing code's own country name — a project legitimately
 * titled "Brand Tracker (US)" has the shape of one but is not a market name, and
 * its market must keep taking its label from its fieldwork locale.
 */
export const isCountryCodedMarketName = (
  name: string | undefined | null
): boolean => {
  const { countryPrefix, countryCode } = splitMarketName(name ?? '')
  return countryCode !== '' && countryPrefix === countryLabel(countryCode)
}
